import { Effect, Layer, Result, Schema } from "effect";
import { HttpClient, HttpClientRequest, type HttpClientResponse } from "effect/unstable/http";
import { HttpApi, HttpApiClient } from "effect/unstable/httpapi";
import { ChatApprovalDecisionResponse } from "@workerstation/shared/chat-contract";
import { ChatApi as SharedChatApi } from "@workerstation/shared/chat/api";
import { ResourceStorageError, resourceUnavailable } from "@workerstation/shared/errors/resource";
import { ChatClientRequestError, ChatStation, ChatThread, ChatProposal, ChatReview, LegacyChatApproval, type ChatClientFailure, type ChatApprovalResult } from "./model.ts";
import { ChatApi } from "./service.ts";
const Api = HttpApi.make("FrontendChatApi").add(SharedChatApi);
const Envelope = Schema.Struct({ error: Schema.optionalKey(Schema.Json) });
const PublicRefusal = Schema.Struct({ error: Schema.String });
const Failure = Schema.Union([ChatClientRequestError, ResourceStorageError]);
const invalid = () => new ChatClientRequestError({ status: 400, message: "Invalid chat request." });
const decode = <S extends Schema.Top>(schema: S, input: unknown) => Schema.decodeUnknownEffect(schema)(input).pipe(Effect.mapError(invalid));
const own = <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.scoped(operation.pipe(
  Effect.mapError((error): ChatClientFailure => Schema.is(Failure)(error) ? error : resourceUnavailable())
));
const refusal = (response: HttpClientResponse.HttpClientResponse): Effect.Effect<never, ChatClientFailure> => {
  const status = response.status;
  if (status !== 400 && status !== 404 && status !== 409 && status !== 413 && status !== 429 && status !== 503) return Effect.fail(resourceUnavailable());
  return response.json.pipe(Effect.flatMap(Schema.decodeUnknownEffect(PublicRefusal)), Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(body => Effect.fail(new ChatClientRequestError({ status, message: body.error }))));
};
export const makeChatApi = Effect.fn("Frontend.ChatApi.make")(function* (baseUrl: string) {
  const http = HttpClient.transformResponse(HttpClient.withScope(yield* HttpClient.HttpClient), response => response.pipe(
    Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(value => value.status >= 200 && value.status < 300 ? value.json.pipe(
      Effect.flatMap(Schema.decodeUnknownEffect(Envelope)), Effect.mapError(() => resourceUnavailable()),
      Effect.flatMap(body => body.error ? Effect.fail(resourceUnavailable()) : Effect.succeed(value))
    ) : refusal(value))
  ));
  const client = yield* HttpApiClient.makeWith(Api, { httpClient: http, baseUrl });
  // Each mutation captures its own original JSON bytes. Shared endpoint validation
  // still runs before transport; an independent client prevents concurrent capture.
  const wire = (body: unknown) => Effect.gen(function* () {
    const text = yield* Effect.try({ try: () => JSON.stringify(body), catch: () => resourceUnavailable() });
    return yield* HttpApiClient.makeWith(Api, { httpClient: HttpClient.mapRequest(http, request => HttpClientRequest.bodyText(request, text, "application/json")), baseUrl });
  });
  return ChatApi.of({
    config: Effect.fn("Frontend.ChatApi.config")(function* (stationId) {
      return yield* client.chat.config({ params: yield* decode(ChatStation, { stationId }) });
    }, own),
    configure: Effect.fn("Frontend.ChatApi.configure")(function* (stationId, patch) {
      const params = yield* decode(ChatStation, { stationId });
      return yield* (yield* wire(patch)).chat.configure({ params, payload: patch });
    }, own),
    threads: Effect.fn("Frontend.ChatApi.threads")(function* (stationId) {
      const result = yield* client.chat.threads({ params: yield* decode(ChatStation, { stationId }) });
      return result.threads;
    }, own),
    createThread: Effect.fn("Frontend.ChatApi.createThread")(function* (stationId) {
      const params = yield* decode(ChatStation, { stationId });
      return yield* (yield* wire({})).chat.createThread({ params, payload: {} });
    }, own),
    tail: Effect.fn("Frontend.ChatApi.tail")(function* (stationId, threadId, afterSequence) {
      const params = yield* decode(ChatThread, { stationId, threadId }), cursor = yield* decode(Schema.Number, afterSequence);
      const result = yield* client.chat.tail({ params, query: { afterSequence: String(cursor) } });
      if (result.rows.some(row => row.threadId !== params.threadId)) return yield* Effect.fail(resourceUnavailable());
      return result;
    }, own),
    message: Effect.fn("Frontend.ChatApi.message")(function* (stationId, threadId, payload) {
      const params = yield* decode(ChatThread, { stationId, threadId });
      return yield* (yield* wire(payload)).chat.message({ params, payload });
    }, own),
    stop: Effect.fn("Frontend.ChatApi.stop")(function* (stationId, threadId) {
      const params = yield* decode(ChatThread, { stationId, threadId });
      return yield* (yield* wire({})).chat.stop({ params });
    }, own),
    approval: Effect.fn("Frontend.ChatApi.approval")(function* (stationId, approvalId, approve) {
      const params = yield* decode(Schema.Struct({ ...ChatStation.fields, approvalId: Schema.String }), { stationId, approvalId });
      const response = yield* (yield* wire({ approve })).chat.approval({ params, payload: { approve }, responseMode: "response-only" });
      if (response.status !== 200) return yield* Effect.fail(resourceUnavailable());
      const body = yield* response.json.pipe(Effect.mapError(() => resourceUnavailable()));
      const current = Schema.decodeUnknownResult(ChatApprovalDecisionResponse)(body);
      if (Result.isSuccess(current)) { const result: ChatApprovalResult = { _tag: "Current", response: current.success }; return result; }
      const legacy = yield* Schema.decodeUnknownEffect(LegacyChatApproval)(body).pipe(Effect.mapError(() => resourceUnavailable()));
      const result: ChatApprovalResult = { _tag: "Legacy", response: legacy }; return result;
    }, own),
    proposal: Effect.fn("Frontend.ChatApi.proposal")(function* (stationId, threadId, proposalId, action) {
      const params = yield* decode(ChatProposal, { stationId, threadId, proposalId });
      return yield* (yield* wire({ action })).chat.proposal({ params, payload: { action } });
    }, own),
    proposalReview: Effect.fn("Frontend.ChatApi.proposalReview")(function* (stationId, threadId, proposalId, worktreeId) {
      const target = yield* decode(ChatReview, { stationId, threadId, proposalId, worktreeId });
      const result = yield* client.chat.proposalReview({ params: target });
      if (result.stationId !== target.stationId || result.worktreeId !== target.worktreeId) return yield* Effect.fail(resourceUnavailable());
      return result;
    }, own)
  });
});
export const ChatApiLive = (baseUrl: string) => Layer.effect(ChatApi, makeChatApi(baseUrl));
